// AI Seeker. Walks the planet using greedy great-circle nav, has a vision cone
// + hearing radius, and remembers last-seen player position for ~6 seconds.
//
// States:
//   PATROL — pick a random waypoint on the planet, walk there, repeat
//   INVESTIGATE — heard a noise; walk to that location and look around
//   CHASE — seen the player; head straight there
//   SEARCH_LAST — lost line of sight; check the last known spot
//
// All movement is on the unit sphere; we slerp the seeker's direction toward
// its target each frame.

import * as THREE from 'three';
import { makeCharacter } from '../player/character.js';

export class AISeeker {
  constructor({ planet, speed = 4.0, chaseSpeed = 6.5, visionRange = 24, visionAngleDeg = 70, hearingRange = 18 }) {
    this.planet = planet;
    this.speed = speed;
    this.chaseSpeed = chaseSpeed;
    this.visionRange = visionRange;
    this.visionCos = Math.cos((visionAngleDeg * Math.PI / 180) / 2);
    this.hearingRange = hearingRange;
    this.dir = new THREE.Vector3(1, 0, 0);
    this.heading = 0;
    this.position = new THREE.Vector3();
    this.up = new THREE.Vector3(0, 1, 0);
    this.forward = new THREE.Vector3();
    this.state = 'PATROL';
    this.targetDir = null;
    this.lastSeenDir = null;
    this.lastSeenAge = 0;
    this.investigateTimer = 0;
    this.searchTimer = 0;
    // Visual — ember-tinted golem
    this.mesh = makeCharacter({ bodyColor: 0x6a3a3a, mossColor: 0x8a4030, eyeColor: 0xff7a4d, isSeeker: true });
    // Flashlight cone projected from "head"
    this.flashlight = new THREE.SpotLight(0xffe6b8, 4.5, 30, Math.PI / 5, 0.3, 1.5);
    this.flashlightTarget = new THREE.Object3D();
    this.mesh.add(this.flashlight);
    this.mesh.add(this.flashlightTarget);
    this.flashlight.target = this.flashlightTarget;
    this.flashlight.position.set(0, 1.6, 0);
    this.flashlightTarget.position.set(0, 1.6, 5);
  }

  spawnAt(lon, lat) {
    this.dir.set(Math.cos(lat) * Math.cos(lon), Math.sin(lat), Math.cos(lat) * Math.sin(lon)).normalize();
    this._pickPatrolTarget();
    this._sync();
  }

  _pickPatrolTarget() {
    const lon = Math.random() * Math.PI * 2;
    const lat = (Math.random() - 0.5) * Math.PI * 0.85;
    this.targetDir = new THREE.Vector3(Math.cos(lat) * Math.cos(lon), Math.sin(lat), Math.cos(lat) * Math.sin(lon)).normalize();
  }

  _sync() {
    this.up.copy(this.dir);
    const r = this.planet.sampleRadius(this.dir);
    this.position.copy(this.dir).multiplyScalar(r);
    this.mesh.position.copy(this.position);
    this.mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), this.up);
    // Forward direction in world space (great-circle tangent toward target)
    if (this.targetDir) {
      const tangent = new THREE.Vector3().copy(this.targetDir)
        .sub(this.dir.clone().multiplyScalar(this.dir.dot(this.targetDir)))
        .normalize();
      this.forward.copy(tangent);
      // Rotate the mesh to face along tangent (heading around local up)
      const right = new THREE.Vector3().crossVectors(this.up, tangent).normalize();
      const m = new THREE.Matrix4().makeBasis(right, this.up, tangent.clone().negate());
      const q = new THREE.Quaternion().setFromRotationMatrix(m);
      this.mesh.quaternion.copy(q);
    }
  }

  // Returns true if the player is within the seeker's vision cone with line-of-sight.
  _canSee(player, props) {
    const toPlayer = new THREE.Vector3().subVectors(player.position, this.position);
    const dist = toPlayer.length();
    if (dist > this.visionRange) return false;
    toPlayer.normalize();
    if (this.forward.dot(toPlayer) < this.visionCos) return false;
    // Crouching halves effective range
    if (player.crouching && dist > this.visionRange * 0.55) return false;
    // Bush occlusion: if a bush is within hideRadius of the player, see-through is blocked
    for (const p of props) {
      if (p.userData.kind === 'bush' && p.userData.hideRadius) {
        const inBush = p.position.distanceTo(player.position) < p.userData.hideRadius;
        if (inBush && dist > 5) return false; // hidden by bush at range
      }
    }
    // Behind-the-planet occlusion: if the seeker and player are on opposite
    // hemispheres relative to the planet center, the body of the planet
    // blocks line-of-sight.
    const seekerDir = this.dir;
    const playerDir = player.dir;
    if (seekerDir.dot(playerDir) < 0.2) return false;
    return true;
  }

  _canHear(player) {
    const noise = player.noiseLevel();
    if (noise <= 0) return false;
    const range = this.hearingRange * noise;
    return this.position.distanceTo(player.position) < range;
  }

  update(dt, player, props, elapsed) {
    // ---- Perception ----
    const seen = this._canSee(player, props);
    const heard = !seen && this._canHear(player);

    if (seen) {
      this.lastSeenDir = player.dir.clone();
      this.lastSeenAge = 0;
      this.state = 'CHASE';
      this.targetDir = player.dir.clone();
    } else if (heard) {
      if (this.state !== 'CHASE') {
        this.state = 'INVESTIGATE';
        this.targetDir = player.dir.clone();
        this.investigateTimer = 4.0;
      }
    } else {
      this.lastSeenAge += dt;
      if (this.state === 'CHASE' && this.lastSeenAge > 1.0) {
        this.state = 'SEARCH_LAST';
        this.targetDir = this.lastSeenDir?.clone() || this.targetDir;
        this.searchTimer = 5.0;
      }
    }

    // ---- State logic ----
    if (this.state === 'INVESTIGATE') {
      this.investigateTimer -= dt;
      if (this.investigateTimer <= 0) {
        this.state = 'PATROL';
        this._pickPatrolTarget();
      }
    } else if (this.state === 'SEARCH_LAST') {
      this.searchTimer -= dt;
      if (this.searchTimer <= 0) {
        this.state = 'PATROL';
        this._pickPatrolTarget();
      }
    } else if (this.state === 'PATROL') {
      if (!this.targetDir || this.dir.angleTo(this.targetDir) < 0.05) {
        this._pickPatrolTarget();
      }
    }

    // ---- Movement: slerp dir toward targetDir ----
    if (this.targetDir) {
      const speed = this.state === 'CHASE' ? this.chaseSpeed : this.speed;
      const r = this.planet.sampleRadius(this.dir);
      // Angular step: arc length = speed*dt; angle = arc/r
      const maxStep = (speed * dt) / r;
      const angle = this.dir.angleTo(this.targetDir);
      const t = Math.min(1, maxStep / Math.max(0.001, angle));
      // Slerp via quaternion
      const axis = new THREE.Vector3().crossVectors(this.dir, this.targetDir);
      if (axis.lengthSq() > 1e-8) {
        axis.normalize();
        const q = new THREE.Quaternion().setFromAxisAngle(axis, angle * t);
        this.dir.applyQuaternion(q).normalize();
      }
    }

    // Flashlight color/intensity reacts to state
    if (this.state === 'CHASE') this.flashlight.color.setHex(0xff7a4a);
    else if (this.state === 'INVESTIGATE' || this.state === 'SEARCH_LAST') this.flashlight.color.setHex(0xffd060);
    else this.flashlight.color.setHex(0xffe6b8);

    this._sync();
  }
}
